import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, Col } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux-toolkit/store";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { getFilteredPositionIds } from "../../../db/DexieServises";

const RoutesCoverage: React.FC = () => {
  const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([]);
  const [chartOptions, setChartOptions] = useState<any>({});
  const [monthsList, setMonthsList] = useState<string[]>([]);

  const positions = useSelector((state: RootState) => state.position.positions);
  const dateRange = useSelector((state: RootState) => state.position.dateRange);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);

  /**
   * Fetch effort report for given position IDs and month
   */
  const fetchEffortReport = useCallback(
    async (positionIds: string, month: string) => {
      const serverUrl = localStorage.getItem("serverUrl");
      const url = `${serverUrl}${API.getEffortReport}`;

      try {
        const { status, data } = await getData(url, true, {
          position_id: positionIds,
          moye: month, // must be in yyyy-MM
        });

        if (status === 200 && Array.isArray(data.data)) {
          return data.data;
        }

        console.error("Failed to fetch effort report data");
        return [];
      } catch (error) {
        console.error("Error fetching effort report:", error);
        return [];
      }
    },
    []
  );

  /**
   * Prepare chart data
   */
  const processChartData = useCallback(async () => {
    if (!monthsList.length) return;

    const positionIds =
      positions.length > 0
        ? positions.map((pos) => pos.id).join(",")
        : (await getFilteredPositionIds("Position", unitId)).join(",");

    const chartValues = {
      allRoutesPercent: [] as number[],
      uniqueRoutesPercent: [] as number[],
    };

    for (const month of monthsList) {
      const apiData = await fetchEffortReport(positionIds, month);

      const allRoutes = apiData.reduce(
        (sum: number, item: any) => sum + parseInt(item.PrimaryMet || "0", 10),
        0
      );
      const uniqueRoutes = apiData.reduce(
        (sum: number, item: any) => sum + parseInt(item.PrimaryVfMet || "0", 10),
        0
      );

      const total = allRoutes + uniqueRoutes;
      const allPercent = total ? (allRoutes / total) * 100 : 0;
      const uniquePercent = total ? (uniqueRoutes / total) * 100 : 0;

      chartValues.allRoutesPercent.push(allPercent);
      chartValues.uniqueRoutesPercent.push(uniquePercent);
    }

    setChartSeries([
      { name: "Covered Routes %", data: chartValues.allRoutesPercent },
      { name: "Unique Routes %", data: chartValues.uniqueRoutesPercent },
    ]);

    setChartOptions({
      chart: {
        type: "line",
        height: 400,
        toolbar: {
          tools: {
            download: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      title: { text: "Routes Coverage (%)" },
      xaxis: {
        categories: monthsList,
        title: { text: "Month" },
      },
      yaxis: {
        title: { text: "Percentage" },
        min: 0,
        max: 100,
        labels: {
          formatter: (val: number) => `${val.toFixed(2)}%`,
        },
      },
      tooltip: {
        y: { formatter: (val: number) => `${val.toFixed(2)}%` },
      },
      colors: ["#ff9800", "#f44336"],
      legend: { position: "top" },
    });
  }, [positions, unitId, monthsList, fetchEffortReport]);

  /**
   * Build months list strictly based on dateRange.start & dateRange.end
   */
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const [startMonth, startYear] = dateRange.start.split("-").map(Number);
      const [endMonth, endYear] = dateRange.end.split("-").map(Number);

      const newMonths: string[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const from = year === startYear ? startMonth : 1;
        const to = year === endYear ? endMonth : 12;

        for (let m = from; m <= to; m++) {
          newMonths.push(`${year}-${m.toString().padStart(2, "0")}`);
        }
      }

      setMonthsList(newMonths);
    } else {
      const currentYear = new Date().getFullYear();
      const allMonths = Array.from({ length: 12 }, (_, i) => `${currentYear}-${(i + 1)
        .toString()
        .padStart(2, "0")}`);
      setMonthsList(allMonths);
    }
  }, [dateRange]);

  /**
   * Refetch chart whenever months or positions change
   */
  useEffect(() => {
    setChartSeries([]);
    setChartOptions({});
    processChartData();
  }, [positions, monthsList, processChartData]);

  return (
    <Col className="col-sm-12 col-xl-6 box-col-6">
      <Card>
        <CardBody>
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={400}
          />
        </CardBody>
      </Card>
    </Col>
  );
};

export default RoutesCoverage;
